import { reactive, readonly } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import logger from 'shared/helpers/logger';
import internalChatCable, {
  INTERNAL_CHAT_EVENTS,
} from 'dashboard/helper/internalChatCable';
import InternalCallsAPI from 'dashboard/api/internalCalls';

// P2P MESH call layer for Internal Chat (1:1 is a mesh of one peer). Reuses the
// proven WebRTC patterns from useWhatsappCallSession but signals over ActionCable
// (InternalChatChannel) and keeps ONE RTCPeerConnection per remote peer.
//
// Glare avoidance: in any pair, the LOWER user id creates the offer; the higher
// id waits for it. Group calls scale to a handful of peers (mesh = N² streams);
// LiveKit SFU is the future upgrade for larger rooms.
//
// Module-level so the singleton cable handler and the widget share one session.
let localStream = null;
// Map<peerUserId, { pc, stream, audioEl, name }>
const peers = new Map();
let boundOnce = false;

const state = reactive({
  status: 'idle', // idle | outgoing | incoming | active
  callId: null,
  chatId: null,
  callType: 'audio',
  isGroup: false,
  peer: null, // 1:1 convenience: the other party { id, name, avatar_url }
  peerIds: [], // mesh: connected remote user ids (reactive for the grid)
  muted: false,
  cameraOff: false,
  iceServers: [],
  streamEpoch: 0, // bump → widget re-binds <video>/<audio>
});

const myId = () => internalChatCable.currentUserId;
const isVideoCall = () => state.callType === 'video';
const getUserMediaForCall = () =>
  navigator.mediaDevices.getUserMedia({ audio: true, video: isVideoCall() });

export const getLocalStream = () => localStream;
export const getRemoteStream = peerId => peers.get(peerId)?.stream || null;
export const hasActiveInternalCall = () => state.status !== 'idle';

const ensureLocalStream = async () => {
  if (!localStream) {
    localStream = await getUserMediaForCall();
    state.streamEpoch += 1;
  }
  return localStream;
};

const refreshPeerIds = () => {
  state.peerIds = Array.from(peers.keys());
};

const attachRemoteAudio = (peerId, stream) => {
  let entry = peers.get(peerId);
  if (!entry) return;
  if (!entry.audioEl) {
    const el = document.createElement('audio');
    el.autoplay = true;
    el.playsInline = true;
    el.style.display = 'none';
    document.body.appendChild(el);
    entry.audioEl = el;
  }
  entry.audioEl.srcObject = stream;
  entry.audioEl.play().catch(() => {});
};

const createPeerConnection = peerId => {
  const pc = new RTCPeerConnection(
    state.iceServers?.length ? { iceServers: state.iceServers } : {}
  );
  const stream = new MediaStream();
  peers.set(peerId, { pc, stream, audioEl: null });
  refreshPeerIds();

  pc.ontrack = event => {
    const tracks = event.streams?.[0]
      ? event.streams[0].getTracks()
      : [event.track];
    tracks.forEach(t => {
      if (!stream.getTracks().includes(t)) stream.addTrack(t);
    });
    attachRemoteAudio(peerId, stream);
    state.streamEpoch += 1;
  };
  pc.onicecandidate = event => {
    if (event.candidate && state.callId) {
      internalChatCable.performCallSignal('signal_ice', {
        call_id: state.callId,
        to_user_id: peerId,
        candidate: event.candidate,
      });
    }
  };
  return pc;
};

const addLocalTracks = pc => {
  if (!localStream) return;
  localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
};

// Establish (or wait for) a mesh connection to one peer. Lower id offers.
const connectToPeer = async peerId => {
  if (!peerId || peerId === myId() || peers.has(peerId)) return;
  await ensureLocalStream();
  const pc = createPeerConnection(peerId);
  addLocalTracks(pc);
  if (myId() < peerId) {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    internalChatCable.performCallSignal('signal_offer', {
      call_id: state.callId,
      to_user_id: peerId,
      sdp: pc.localDescription.sdp,
    });
  }
  // higher id: just wait for the incoming offer (handled in onOffer)
};

const closePeer = peerId => {
  const entry = peers.get(peerId);
  if (!entry) return;
  try {
    entry.pc.close();
  } catch (_) {
    /* noop */
  }
  entry.stream?.getTracks().forEach(t => t.stop());
  if (entry.audioEl) entry.audioEl.srcObject = null;
  peers.delete(peerId);
  refreshPeerIds();
  state.streamEpoch += 1;
};

const cleanup = () => {
  Array.from(peers.keys()).forEach(closePeer);
  if (localStream) localStream.getTracks().forEach(t => t.stop());
  localStream = null;
  state.status = 'idle';
  state.callId = null;
  state.chatId = null;
  state.isGroup = false;
  state.peer = null;
  state.peerIds = [];
  state.muted = false;
  state.cameraOff = false;
  state.iceServers = [];
  state.streamEpoch += 1;
};

export function useInternalCall() {
  const startCall = async (chat, { callType = 'audio' } = {}) => {
    if (state.status !== 'idle') return;
    try {
      const { data } = await InternalCallsAPI.initiate(chat.id, { callType });
      state.status = 'outgoing';
      state.callId = data.id;
      state.chatId = chat.id;
      state.callType = callType;
      state.isGroup = chat.chat_type !== 'direct_message';
      state.peer = chat.other_user || null;
      state.iceServers = data.ice_servers || [];
    } catch (e) {
      logger.error('[InternalCall] initiate failed', e);
      cleanup();
      throw e;
    }
  };

  const answerCall = async () => {
    if (state.status !== 'incoming' || !state.chatId || !state.callId) return;
    await ensureLocalStream();
    await InternalCallsAPI.answer(state.chatId, state.callId);
    state.status = 'active';
    // Connect to the initiator immediately; other active peers connect when
    // they receive our call.answered broadcast.
    if (state.peer?.id) connectToPeer(state.peer.id);
  };

  const declineCall = async () => {
    if (!state.chatId || !state.callId) return;
    try {
      await InternalCallsAPI.decline(state.chatId, state.callId);
    } finally {
      cleanup();
    }
  };

  const endCall = async () => {
    const { chatId, callId } = state;
    cleanup();
    if (chatId && callId) {
      await InternalCallsAPI.end(chatId, callId).catch(() => {});
    }
  };

  const toggleMute = () => {
    if (!localStream) return;
    state.muted = !state.muted;
    localStream.getAudioTracks().forEach(t => {
      t.enabled = !state.muted;
    });
  };

  const toggleCamera = () => {
    if (!localStream) return;
    const videoTracks = localStream.getVideoTracks();
    if (!videoTracks.length) return;
    state.cameraOff = !state.cameraOff;
    videoTracks.forEach(t => {
      t.enabled = !state.cameraOff;
    });
  };

  return {
    state: readonly(state),
    startCall,
    answerCall,
    declineCall,
    endCall,
    toggleMute,
    toggleCamera,
    getLocalStream,
    getRemoteStream,
  };
}

// --- Cable signaling (module-level, runs outside any component instance) -----
const onOffer = async data => {
  const peerId = data.from_user_id;
  let entry = peers.get(peerId);
  if (!entry) {
    await ensureLocalStream();
    const pc = createPeerConnection(peerId);
    addLocalTracks(pc);
    entry = peers.get(peerId);
  }
  await entry.pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
  const answer = await entry.pc.createAnswer();
  await entry.pc.setLocalDescription(answer);
  internalChatCable.performCallSignal('signal_answer', {
    call_id: state.callId,
    to_user_id: peerId,
    sdp: entry.pc.localDescription.sdp,
  });
};

const onAnswer = async data => {
  const entry = peers.get(data.from_user_id);
  if (entry)
    await entry.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
};

const onIce = async data => {
  const entry = peers.get(data.from_user_id);
  if (entry && data.candidate) {
    await entry.pc.addIceCandidate(data.candidate).catch(() => {});
  }
};

const handleCallEvent = async data => {
  const me = myId();
  // Signaling messages targeted at a specific peer — ignore if not for me.
  if (data.to_user_id != null && data.to_user_id !== me) return;

  switch (data.event) {
    case 'call.initiated':
      if (data.initiator?.id === me || state.status !== 'idle') return;
      state.status = 'incoming';
      state.callId = data.call_id;
      state.chatId = data.chat_id;
      state.callType = data.call_type;
      state.isGroup = data.is_group || false;
      state.peer = data.initiator || null;
      state.iceServers = data.ice_servers || [];
      break;
    case 'call.answered': {
      const responderId = data.responder?.id;
      if (responderId === me) return; // own echo
      if (data.ice_servers?.length) state.iceServers = data.ice_servers;
      // Caller transitions to active on first answer.
      if (state.status === 'outgoing') state.status = 'active';
      // Any active participant connects to the newcomer.
      if (state.status === 'active' && responderId) connectToPeer(responderId);
      break;
    }
    case 'call.declined':
      // 1:1: declining ends the call. Group: just that peer didn't join.
      if (!state.isGroup && state.callId === data.call_id) cleanup();
      break;
    case 'call.ended':
      if (state.callId === data.call_id) cleanup();
      break;
    case 'call.signal.offer':
      if (data.from_user_id !== me) await onOffer(data);
      break;
    case 'call.signal.answer':
      if (data.from_user_id !== me) await onAnswer(data);
      break;
    case 'call.signal.ice':
      if (data.from_user_id !== me) await onIce(data);
      break;
    default:
      break;
  }
};

export const bindInternalCallEvents = () => {
  if (boundOnce) return;
  boundOnce = true;
  emitter.on(INTERNAL_CHAT_EVENTS.CALL_EVENT, handleCallEvent);
};
